// Objectionable-content filter for the small amount of user-generated text in
// the app: team names and smack talk.
//
// App Store Guideline 1.2 requires a filter, not a perfect one. This is a
// deliberately conservative wordlist check: it catches the slurs and the
// obvious abuse that would get the app pulled, and it stays quiet on ordinary
// trash talk, which is the entire point of the smack talk field. A filter that
// rejected "you're getting destroyed this week" would be a worse product and
// would not make Apple any happier.
//
// Matching is on word boundaries after normalization, so the classic
// substring false positives ("Scunthorpe", "Cockburn", "assassin", and the
// several real school nicknames that contain them) pass. Normalization folds
// the usual character substitutions, because "a$$hole" is not a different word
// from the reviewer's point of view.

// Leet and punctuation substitutions, applied before matching.
const SUBSTITUTIONS = [
  [/[@4]/g, "a"],
  [/[3]/g, "e"],
  [/[1!|]/g, "i"],
  [/[0]/g, "o"],
  [/[$5]/g, "s"],
  [/[7]/g, "t"],
];

// Slurs and sexual abuse terms only. General profanity ("damn", "hell",
// "crap", and the common four-letter words used as emphasis) is intentionally
// absent: it is normal register for trash talk between friends and blocking it
// would make the field useless. The terms here are the ones that are abusive
// in every context.
const BLOCKED = [
  "nigger", "nigga", "faggot", "fag", "tranny", "kike", "spic", "chink",
  "wetback", "gook", "retard", "retarded", "cunt", "rapist", "rape",
  "molest", "molester", "pedo", "pedophile", "paedophile", "kys",
  "childporn",
];

function normalize(text) {
  let out = String(text)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "");
  for (const [pattern, replacement] of SUBSTITUTIONS) {
    out = out.replace(pattern, replacement);
  }
  // Collapse the separators used to break a word up ("n.i.g" and friends) but
  // keep whitespace, which is what the word boundaries below rely on.
  return out.replace(/[^a-z0-9\s]/g, "");
}

/**
 * Returns the first blocked term found in `text`, or null if it is clean.
 * Exported mainly so the tests can assert on which term matched.
 */
export function findBlockedTerm(text) {
  if (!text) return null;
  const normalized = normalize(text);

  for (const term of BLOCKED) {
    // Word boundary on both sides: catches "you fag", "fag." and "FAG", but
    // not "faggot" hiding inside a legitimate longer word, of which there are
    // none, and not "assassin" style substring hits, of which there are many.
    if (new RegExp(`\\b${term}\\b`).test(normalized)) return term;
    // Same term with the separators already stripped, so "f a g g o t" and
    // "f-a-g-g-o-t" do not walk straight through.
    if (normalized.replace(/\s+/g, "").includes(term) && term.length >= 5) return term;
  }
  return null;
}

export function containsProfanity(text) {
  return findBlockedTerm(text) !== null;
}

// The single message shown wherever a write is rejected. Deliberately does not
// name the term it matched: quoting the slur back to the user in an error
// banner is worse than saying nothing.
export const PROFANITY_MESSAGE =
  "That wording breaks the league rules on abusive content. Please choose something else.";

export default containsProfanity;
